import React, { useEffect, useRef, useState } from 'react';
import withStyles from "isomorphic-style-loader/withStyles";
import cx from 'classnames';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { reduxForm, formValueSelector } from 'redux-form'
import { Spinner } from 'react-bootstrap';
import { toastr } from 'react-redux-toastr';
import Link from '../Link/Link';
import LottieImage from '../Common/LottieImage/LottieImage';
import messages from '../../locale/messages';
import history from '../../history';
import { closeHeaderModal } from '../../actions/siteadmin/modalActions';
import { getBooking } from '../../actions/booking/getBooking';
import { cancelBookingAction } from '../../actions/booking/cancelBookingAction';
import { getBookingById } from '../../lib/graphql/booking';
import noServiceProviderIcon from '/public/dashboard/noServiceProviderIcon.svg';
import animationData from '../../../public/lottie/nearestProviderAnimationData.json'
import bt from '../commonStyle.css';
import s from './NearestProviderForm.css';

function NearestProviderForm({ socket }) {

    const [declined, setDeclined] = useState(false)
    const [onBoard, setOnBoard] = useState(false)
    const bookingError = useSelector(state => state?.booking?.data?.error)
    // LOCKED (booking flow — verified working 2026-06-12, see docs/web-booking-flow.md §0): this flag
    // is THE thing that keeps a PREFERRED booking from "sauting". true = wait for the chosen worker, NO
    // board timer; false = broadcast, arm the 60s board fallback below. A preferred booking that reaches
    // this screen with isPreffered=false would jump to the board after 60s. Do not change without Paul.
    const isPreffered = useSelector(state => state?.adminModalStatus?.isPreffered)
    const id = useSelector(state => formValueSelector('ConfirmBookingBanner')(state, 'bookingId'))
    const dispatch = useDispatch()
    const { formatMessage } = useIntl()
    // Guard so acceptance is handled once (socket fast-path OR polling fallback can both fire).
    const handledRef = useRef(false)

    // On-board messaging, 1:1 with the app: a PREFERRED booking that fell through shows the reassuring
    // "Worker unavailable — we're assigning another at the same rate" copy (app _showPreferredUnavailableDialog),
    // a CHOOSE-FOR-ME broadcast shows "We're finding your pro — sent to nearby pros" (app auto_on_board).
    const boardTitleMsg = isPreffered ? messages.requestDeclinedLabel : messages.jobOnBoardTitle;
    const boardTextMsg = isPreffered ? messages.preferredProviderDeclineLabel : messages.jobOnBoardText;

    useEffect(() => {
        if (socket && id) {
            // Fast path. besthand-api acceptBooking emits BOTH serviceAccepted-<id> and
            // bookingStatusUpdated-<id>; listen to both since the worker accepts on the OTHER backend
            // and the payload shape can differ. The polling fallback below covers any missed socket.
            socket.on(`bookingStatusUpdated-${id}`, (value) => {
                let socketValue;
                try { socketValue = JSON.parse(value); } catch (e) { socketValue = value; }
                const bookingStatus = socketValue?.data?.data?.bookingStatus || socketValue?.data?.bookingStatus || socketValue?.bookingStatus;
                if (bookingStatus === "declined") {
                    setDeclined(true)
                } else if (bookingStatus === "approved") {
                    handleBookingApproved();
                }
            });
            socket.on(`serviceAccepted-${id}`, () => {
                handleBookingApproved();
            });
            // No worker accepted within the 1-min window: the backend posts the job to the board
            // (visible to all nearby pros). Mirror the mobile UX — tell the client and send them home.
            socket.on(`preferredUnavailable-${id}`, () => {
                setOnBoard(true);
            });
        }
    }, [socket, id])

    // ── Socket-INDEPENDENT polling fallback (parity with the mobile connecting screen). Sockets/FCM
    // are unreliable in production AND the acceptance comes from the besthand-api backend, so we poll
    // the booking every 5s: once a worker is assigned (partnerId set and status moved past
    // created/pending), go straight to tracking — no socket required.
    const { data: polledData } = useQuery(getBookingById, {
        variables: { bookingId: id ? Number(id) : null },
        skip: !id || onBoard || declined,
        fetchPolicy: 'network-only',
        ssr: false,
        pollInterval: 5000,
    });

    useEffect(() => {
        const b = polledData?.getBookingById?.result;
        if (!b) return;
        // LOCKED (booking flow): a PREFERRED booking is created with partnerId ALREADY set on
        // status 'created' (the chosen worker). 'created' MUST stay in this exclude list or the very
        // first poll would read partnerId+'created' as "accepted" and the preferred screen would
        // saute straight to /tracking before the worker even answers. Real accept moves status past it.
        const accepted = b.partnerId && !['created', 'pending', 'scheduled', 'cancelled'].includes(b.status);
        if (accepted) handleBookingApproved();
    }, [polledData])

    // Broadcast ("Choose for me") 1-min board fallback — if no worker has accepted within a minute we
    // mirror mobile: tell the client the job is on the board and send them home. Preferred bookings
    // instead wait for the preferredUnavailable socket (the besthand-api fallback cron emits it).
    // LOCKED (booking flow): the 60s board jump is BROADCAST-ONLY. `isPreffered` short-circuits it so a
    // PREFERRED booking never auto-sautes to the board — it waits for the chosen worker (accept via
    // poll/socket) or the besthand-api preferredFallbackCron's preferredUnavailable socket. Keep the guard.
    useEffect(() => {
        if (isPreffered || !id) return undefined;
        const t = setTimeout(() => {
            if (!handledRef.current) setOnBoard(true);
        }, 60000);
        return () => clearTimeout(t);
    }, [isPreffered, id])

    const handleBookingApproved = async () => {
        if (handledRef.current) return;
        handledRef.current = true;
        // Popup notification, parity with the app's "Booking confirmed" dialog (_showWorkerAcceptedDialog).
        toastr.success(formatMessage(messages.requestAccepted), formatMessage(messages.requestAcceptedContent));
        await dispatch(getBooking(id));
        history.push(`/tracking/${id}`);
        dispatch(closeHeaderModal('nearestProviderForm'));
    };

    // "booking s'ouvre le job": after the on-board popup, take the client to the booking's tracking
    // page. It renders the searching/pending state (ProviderDetails is null-safe with no worker) and
    // flips to the assigned worker via its bookingStatusUpdated socket once someone accepts from the
    // board — parity with the app navigating to trackOrder instead of dumping the client home.
    const goToJob = () => {
        dispatch(closeHeaderModal('nearestProviderForm'));
        history.push(`/tracking/${id}`);
    };

    useEffect(() => {
        if (!onBoard) return undefined;
        // Popup notification, parity with the app dialog. Hold ~6s so the client reads the reassurance,
        // then open the job's tracking page (searching state).
        toastr.info(formatMessage(boardTitleMsg), formatMessage(boardTextMsg));
        const t = setTimeout(goToJob, 6000);
        return () => clearTimeout(t);
    }, [onBoard])

    if (onBoard) {
        // Parity with the app's centered dialog: title + reassuring copy + spinner, held ~12s then home.
        return (
            <div className={bt.textAlignCenter}>
                <div className={cx(bt.fontWeight700, bt.commonSize20, bt.primaryColor, bt.textAlignCenter, bt.space1)}>{formatMessage(boardTitleMsg)}</div>
                <div className={cx(bt.commonSize14, bt.textAlignCenter, bt.space3)}>{formatMessage(boardTextMsg)}</div>
                <div className={cx(bt.textAlignCenter, bt.space3)}>
                    <Spinner animation="border" role="status" variant="primary" />
                </div>
            </div>
        );
    }

    if (declined) {
        return (
            <div>
                <div className={s.nearestNoServiceImgContainer}>
                    <img src={noServiceProviderIcon} alt="No Service Provider" />
                </div>
                {isPreffered && <div className={cx(bt.fontWeight700, bt.commonSize20, bt.primaryColor, bt.textAlignCenter, bt.space1)}>{formatMessage(messages.requestDeclinedLabel)}</div>}
                {isPreffered ? <div className={cx(bt.commonSize14,bt.textAlignCenter)}><FormattedMessage {...messages.preferredProviderDeclineLabel} /> </div> : <div className={cx(bt.fontWeight700, bt.commonSize20, bt.primaryColor, bt.textAlignCenter)}><FormattedMessage {...messages.noServiceProvider} /></div>}
            </div>
        );
    }

    return (
        <>
            {
                (bookingError === 'Sorry, no service providers available for your order. Please try again.') ?
                    <div>
                        <div className={s.nearestNoServiceImgContainer}>
                            <img src={noServiceProviderIcon} alt="No Service Provider" />
                        </div>
                        <div className={cx(bt.fontWeight700, bt.commonSize20, bt.primaryColor, bt.textAlignCenter)}>
                            {isPreffered ? <FormattedMessage {...messages.currencyNoProvidersAvailable} /> : <FormattedMessage {...messages.noServiceProvider} />}
                        </div>
                    </div> :
                    <div>
                        <div className={s.nearestProviderImgContainer}>
                            <LottieImage animationData={animationData} bookingError={bookingError} />
                        </div>
                        <div className={cx(bt.fontWeight700, bt.commonSize20, bt.primaryColor, bt.textAlignCenter, bt.space3)}>
                            {isPreffered ? <FormattedMessage {...messages.connectPreferredProviderText} /> : <FormattedMessage {...messages.connectProviderText} />}
                        </div>
                        {!isPreffered &&
                            <div className={cx(bt.commonSize14, bt.textAlignCenter, bt.space3)}>
                                <FormattedMessage {...messages.connectProviderWaitHint} />
                            </div>
                        }
                        <div className={s.nearestProviderLinkContainer}>
                            <Link to="#" onClick={() => dispatch(cancelBookingAction({ bookingId: id }))} className={bt.viewAllLink}>
                                <FormattedMessage {...messages.cancelBooking} />
                            </Link>
                        </div>
                    </div>
            }
        </>
    )
}
NearestProviderForm = reduxForm({
    form: 'NearestProviderForm',
})(NearestProviderForm);


export default withStyles(bt, s)(NearestProviderForm);
